import React, { useEffect, useState, useRef, useCallback } from "react";
import { uploadChunked } from "../lib/upload";
import { authStore } from "../lib/api";
import { toast } from "./ui/sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Inbox, Loader2, Upload, FileCheck2, Send, Users, MessageSquare, ChevronDown, ChevronUp, Paperclip, X, Check, Undo2, ClipboardCheck, QrCode, Eye } from "lucide-react";
import SurveyIntakeMirror from "./SurveyIntakeMirror";
import SalesProcessMirror from "./SalesProcessMirror";
import AttachmentLightbox from "./AttachmentLightbox";
/* 概念方案属设计部流程，展示时间固定洛杉矶口径（2026-10-29 时区统一）；
   同时接入主线新增的 FileUploadDate（文件真实上传日，取文件元数据）。 */
import { fmtDT } from "../lib/usDate";
import FileUploadDate from "../pages/design/v3/FileUploadDate";
// 概念方案上传（2026-10-20）：营销方案常以 PPT / CDR / CAD 源文件提交，
// 统一放开到设计部共享白名单，与阶段产物、评论附件口径一致。
import { DESIGN_UPLOAD_ACCEPT } from "../lib/uploadAccept";

import { T } from "../lib/i18n";

const STATUS = {
  in_design: { zh: "设计中", cls: "bg-blue-100 text-blue-700" },
  submitted: { zh: "已提交待发送", cls: "bg-amber-100 text-amber-700" },
  sent: { zh: "已发客户", cls: "bg-violet-100 text-violet-700" },
  client_agreed: { zh: "客户已同意", cls: "bg-emerald-100 text-emerald-700" },
  client_revision: { zh: "客户提出修改", cls: "bg-red-100 text-red-700" },
};

function BriefDiscussion({ api, leadId, zh }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attIdx, setAttIdx] = useState(null);
  const fileRef = useRef(null);
  const dl = (fid) => `${api.defaults.baseURL}/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
  const allAtts = notes.flatMap((n) => n.attachments || []);
  const openAtt = (a) => { const i = allAtts.findIndex((x) => (x.aid || x.fileId) === (a.aid || a.fileId)); setAttIdx(i >= 0 ? i : 0); };
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/design/concept-briefs/${leadId}/notes`); setNotes(data || []); } catch { /* */ }
    api.post(`/crm/leads/${leadId}/discussion/read`).catch(() => {});
  }, [api, leadId]);
  useEffect(() => { load(); }, [load]);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fin = await uploadChunked(api, f);
        setPending((p) => [...p, { fileId: fin.fileId, filename: f.name, contentType: f.type }]);
      }
    } catch { toast.error(zh ? "上传失败" : "Upload failed"); }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
  };
  const send = async () => {
    if (!text.trim() && !pending.length) return;
    setBusy(true);
    try { await api.post(`/crm/leads/${leadId}/notes`, { text, mentions: [], attachments: pending }); setText(""); setPending([]); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className="mt-2" data-testid={`concept-discussion-${leadId}`}>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {notes.length === 0 ? <p className="text-[11px] text-slate-300 text-center py-2">{zh ? "暂无讨论" : "No messages"}</p> :
          notes.map((n) => (
            <div key={n.id} className="text-[12px]">
              <span className="font-semibold text-slate-600">{n.authorName}</span>
              <span className="text-[10px] text-slate-400 ml-1.5">{(n.createdAt || "").slice(5, 16).replace("T", " ")}</span>
              {n.text && <p className="text-slate-600 whitespace-pre-wrap leading-tight">{T(n.text)}</p>}
              {(n.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`concept-note-atts-${n.id}`}>
                  {n.attachments.map((a, i) => (
                    <div key={a.aid || a.fileId} data-testid={`concept-note-attachment-${n.id}-${i}`} className="flex max-w-full flex-col items-start">
                      {(a.contentType || "").startsWith("image/") ? (
                        <button type="button" onClick={() => openAtt(a)} title={a.filename} data-testid={`concept-note-att-${a.aid || a.fileId}`}><img src={dl(a.fileId)} alt={a.filename} className="h-14 w-14 object-cover rounded-md border border-slate-200 hover:opacity-80" /></button>
                      ) : (
                        <button type="button" onClick={() => openAtt(a)} data-testid={`concept-note-att-${a.aid || a.fileId}`} className="inline-flex items-center gap-1 text-[11px] text-indigo-600 border border-indigo-100 rounded-md px-2 py-1 hover:bg-indigo-50"><Paperclip className="h-3 w-3" />{a.filename}</button>
                      )}
                      <FileUploadDate value={a.uploadedAt} className="block" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`concept-note-pending-${leadId}`}>
          {pending.map((a, i) => (
            <div key={i} className="relative">
              {(a.contentType || "").startsWith("image/")
                ? <img src={dl(a.fileId)} alt={a.filename} className="h-12 w-12 object-cover rounded-md border border-slate-200" />
                : <div className="h-12 w-12 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-[9px] text-slate-500 p-1 text-center">{(a.filename || "file").split(".").pop().toUpperCase()}</div>}
              <button onClick={() => setPending((p) => p.filter((_, j) => j !== i))} data-testid={`concept-note-pending-remove-${leadId}-${i}`} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-1.5">
        <input ref={fileRef} type="file" multiple className="hidden" data-testid={`concept-note-file-${leadId}`} onChange={onPick} accept={DESIGN_UPLOAD_ACCEPT} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`concept-note-attach-${leadId}`} className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50">{uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}</button>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid={`concept-note-input-${leadId}`} placeholder={zh ? "回复设计讨论（可加附件，与内部讨论同步）" : "Reply (attach files supported)"}
          className="flex-1 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        <button onClick={send} disabled={busy} data-testid={`concept-note-send-${leadId}`} className="p-1.5 rounded-lg bg-indigo-600 text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </button>
      </div>
      {attIdx !== null && allAtts[attIdx] && (
        <AttachmentLightbox items={allAtts} index={attIdx} onClose={() => setAttIdx(null)} onIndex={setAttIdx} urlOf={dl} zh={zh} />
      )}
    </div>
  );
}


function BriefCard({ api, zh, b, canSubmit, canManage, roster, onChange }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState([]);
  const [cnSel, setCnSel] = useState("");
  const [usSel, setUsSel] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const fileRef = useRef(null);
  const st = STATUS[b.status] || STATUS.in_design;
  const cnRoster = (roster || []).filter((r) => (r.tracks ? r.tracks.includes("cn") : r.role === "cn_architect"));
  const usRoster = (roster || []).filter((r) => (r.tracks ? r.tracks.includes("us") : ["us_policy", "design_manager"].includes(r.role)));
  const LABELS = zh ? ["总平面图", T("平面图"), T("3D效果图"), T("立面图"), T("其他")] : ["Site plan", "Floor plan", "3D render", "Elevation", "Other"];

  const onPick = (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setPending((prev) => [...prev, ...files.map((f, i) => ({ name: f.name, file: f, label: LABELS[Math.min(prev.length + i, LABELS.length - 1)] }))]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const setLabel = (idx, v) => setPending((p) => p.map((x, i) => (i === idx ? { ...x, label: v } : x)));
  const removePending = (idx) => setPending((p) => p.filter((_, i) => i !== idx));

  const submitSet = async () => {
    if (!pending.length) return;
    setUploading(true);
    try {
      const items = [];
      for (const it of pending) {
        const fin = await uploadChunked(api, it.file);
        items.push({ fileId: fin.fileId, label: it.label });
      }
      await api.post(`/crm/leads/${b.leadId}/concept-design/submit`, { files: items });
      toast.success(zh ? `已提交整套概念方案（${items.length} 张）` : `Submitted ${items.length} file(s)`);
      setPending([]); onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setUploading(false);
  };

  const reassign = async () => {
    const body = {};
    if (cnSel) body.cnAssignee = cnSel;
    if (usSel) body.usAssignee = usSel;
    if (!Object.keys(body).length) return;
    try { await api.post(`/crm/leads/${b.leadId}/concept-design/reassign`, body); toast.success(zh ? "已改派" : "Reassigned"); setCnSel(""); setUsSel(""); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "改派失败" : "Failed")); }
  };

  const taskDone = async (tid) => {
    try { await api.post(`/crm/leads/${b.leadId}/concept-design/revision-task/${tid}/done`); toast.success(zh ? "已标记完成" : "Done"); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
  };

  const review = async (decision) => {
    let note = "";
    if (decision === "rejected") {
      note = window.prompt(zh ? "退回修改说明（会通知设计师）：" : "Revision note (will notify designer):", "") || "";
    }
    try {
      await api.post(`/crm/leads/${b.leadId}/concept-design/review`, { decision, note });
      toast.success(decision === "approved" ? (zh ? "已通过审核" : "Approved") : (zh ? "已退回设计师" : "Rejected"));
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
  };
  const REVIEW = { pending: { zh: T("待审核"), cls: "bg-amber-100 text-amber-700" },
    approved: { zh: T("审核通过"), cls: "bg-emerald-100 text-emerald-700" },
    rejected: { zh: T("已退回"), cls: "bg-red-100 text-red-700" } };

  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-3 ${open ? "md:col-span-2" : ""}`} data-testid={`concept-brief-${b.leadId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-800">{b.clientName || "—"}</p>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.cls}`}>{zh ? st.zh : b.status}</span>
        {b.reviewStatus && REVIEW[b.reviewStatus] && <span className={`text-[10px] px-2 py-0.5 rounded-full ${REVIEW[b.reviewStatus].cls}`} data-testid={`concept-review-badge-${b.leadId}`}>{zh ? REVIEW[b.reviewStatus].zh : b.reviewStatus}</span>}
        {b.shareToken && (
          <button onClick={(e) => { e.stopPropagation(); setQrOpen(true); }} data-testid={`concept-brief-share-${b.leadId}`}
            className="text-[10px] text-violet-600 border border-violet-200 rounded-full px-2 py-0.5 hover:bg-violet-50 inline-flex items-center gap-0.5">
            <QrCode className="h-3 w-3" />{zh ? "客户网页/二维码" : "Client page/QR"}
          </button>
        )}
        {b.shareToken && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-0.5 ${b.viewCount ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-400"}`} data-testid={`concept-brief-views-${b.leadId}`}
            title={b.lastViewedAt ? `${zh ? "最近查看" : "Last viewed"} ${fmtDT(b.lastViewedAt)}` : ""}>
            <Eye className="h-3 w-3" />
            {b.viewCount ? (zh ? `业主打开 ${b.viewCount} 次` : `${b.viewCount}× viewed`) : (zh ? "未打开" : "Not viewed")}
            {b.lastViewedAt && <span className="opacity-70">· {String(b.lastViewedAt).slice(5, 16).replace("T", " ")}</span>}
          </span>
        )}
        {b.unread > 0 && <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5">{b.unread}</span>}
        <button onClick={() => setOpen((v) => !v)} data-testid={`concept-toggle-${b.leadId}`} className="ml-auto text-indigo-600 hover:text-indigo-800 text-xs inline-flex items-center gap-1 font-medium">
          {open ? <>{zh ? "收起" : "Less"}<ChevronUp className="h-3.5 w-3.5" /></> : <>{zh ? "展开处理" : "Open"}<ChevronDown className="h-3.5 w-3.5" /></>}
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mt-0.5">{b.address}{b.city ? ` · ${b.city}` : ""}</p>
      <p className="text-[11px] text-slate-400 inline-flex items-center gap-1 mt-0.5"><Users className="h-3 w-3" />{zh ? "中" : "CN"}:{b.cnAssigneeName || "—"} · {zh ? "美" : "US"}:{b.usAssigneeName || "—"}</p>

      {open && (
        <div className="mt-2 space-y-2" data-testid={`concept-detail-${b.leadId}`}>
          <SurveyIntakeMirror api={api} leadId={b.leadId} zh={zh} />
          <SalesProcessMirror api={api} leadId={b.leadId} zh={zh} />
          {b.requirements ? (
            <div><p className="text-[11px] font-semibold text-slate-500">{zh ? "设计要求（销售填写 · 中/美设计共享）" : "Design requirements (from sales)"}</p>
              <p className="text-[12px] text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2 max-h-40 overflow-y-auto" data-testid={`concept-req-${b.leadId}`}>{b.requirements}</p>
              {b.dispatchedAt && <p className="text-[10px] text-slate-400 mt-0.5">{zh ? `下达时间 ${fmtDT(b.dispatchedAt)}${b.dispatchedBy ? ` · ${b.dispatchedBy}` : ""}` : fmtDT(b.dispatchedAt)}</p>}
            </div>
          ) : (<>
            {b.requirementsCn && (
              <div><p className="text-[11px] font-semibold text-slate-500">{zh ? "中国方案设计要求" : "CN brief"}</p>
                <p className="text-[12px] text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2 max-h-40 overflow-y-auto" data-testid={`concept-req-cn-${b.leadId}`}>{b.requirementsCn}</p></div>
            )}
            {b.requirementsUs && (
              <div><p className="text-[11px] font-semibold text-slate-500">{zh ? "美国配合要求（法规/现场复核）" : "US brief"}</p>
                <p className="text-[12px] text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2 max-h-40 overflow-y-auto" data-testid={`concept-req-us-${b.leadId}`}>{b.requirementsUs}</p></div>
            )}
          </>)}

          {(b.finalFiles || []).length > 0 && (
            <div className="space-y-0.5" data-testid={`concept-final-files-${b.leadId}`}>
              {b.finalFiles.map((f, i) => (
                <div key={f.fileId || i} data-testid={`concept-final-file-${b.leadId}-${i}`} className="flex flex-col items-start">
                  <p className="text-[11px] text-emerald-600 inline-flex items-center gap-1 mr-3"><FileCheck2 className="h-3.5 w-3.5" />{f.label ? `${T(f.label)}：` : ""}{f.filename}</p>
                  <FileUploadDate value={f.uploadedAt} className="block" />
                </div>
              ))}
            </div>
          )}

          {(b.revisionTasks || []).length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2" data-testid={`concept-revtasks-${b.leadId}`}>
              <p className="text-[11px] font-semibold text-red-700 mb-1">{zh ? "客户修改意见（待处理）" : "Client revision to-dos"}</p>
              <div className="space-y-1.5">
                {b.revisionTasks.map((t) => (
                  <div key={t.id} className="flex items-start gap-2 text-[12px]" data-testid={`concept-revtask-${t.id}`}>
                    <button onClick={() => taskDone(t.id)} data-testid={`concept-revtask-done-${t.id}`} title={zh ? "标记完成" : "Done"}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border border-red-300 hover:bg-emerald-500 hover:border-emerald-500 flex items-center justify-center text-white">✓</button>
                    <div className="flex-1">
                      <span className="text-slate-700">{t.comment || (zh ? "（仅手绘修改）" : "(drawing only)")}</span>
                      {t.drawingFileId && <span className="text-[10px] text-red-500 ml-1">· {zh ? "含手绘" : "drawing"}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canSubmit && (
            <div className="space-y-2">
              <input ref={fileRef} type="file" multiple className="hidden" data-testid={`concept-upload-input-${b.leadId}`} onChange={onPick} accept={DESIGN_UPLOAD_ACCEPT} />
              {pending.length > 0 && (
                <div className="space-y-1.5 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2" data-testid={`concept-pending-${b.leadId}`}>
                  <p className="text-[11px] font-semibold text-emerald-700">{zh ? "待提交整套方案（可给每张打标签）" : "Files to submit (label each)"}</p>
                  {pending.map((it, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`concept-pending-row-${b.leadId}-${i}`}>
                      <span className="text-[11px] text-slate-600 flex-1 truncate">{it.name}</span>
                      <select value={it.label} onChange={(e) => setLabel(i, e.target.value)} data-testid={`concept-pending-label-${b.leadId}-${i}`} className="text-[11px] border border-slate-200 rounded px-1.5 py-1">
                        {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <button onClick={() => removePending(i)} data-testid={`concept-pending-remove-${b.leadId}-${i}`} className="text-slate-400 hover:text-red-500 text-xs px-1">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`concept-upload-btn-${b.leadId}`}
                  className="inline-flex items-center gap-1.5 text-xs text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded-lg px-3 py-1.5 disabled:opacity-50">
                  <Upload className="h-3.5 w-3.5" />{zh ? "添加图纸（可多选）" : "Add files"}
                </button>
                {pending.length > 0 && (
                  <button onClick={submitSet} disabled={uploading} data-testid={`concept-submit-set-${b.leadId}`}
                    className="inline-flex items-center gap-1.5 text-xs text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-1.5 disabled:opacity-50">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />}{zh ? `提交整套方案（${pending.length}）` : `Submit set (${pending.length})`}
                  </button>
                )}
              </div>
            </div>
          )}

          {canManage && (roster || []).length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-100" data-testid={`concept-reassign-${b.leadId}`}>
              <span className="text-[11px] text-slate-400">{zh ? "改派：" : "Reassign:"}</span>
              <select value={cnSel} onChange={(e) => setCnSel(e.target.value)} data-testid={`concept-cn-select-${b.leadId}`} className="text-[11px] border border-slate-200 rounded px-1.5 py-1">
                <option value="">{zh ? "中国负责人" : "CN"}</option>
                {cnRoster.map((r) => <option key={r.username} value={r.username}>{T(r.name)}</option>)}
              </select>
              <select value={usSel} onChange={(e) => setUsSel(e.target.value)} data-testid={`concept-us-select-${b.leadId}`} className="text-[11px] border border-slate-200 rounded px-1.5 py-1">
                <option value="">{zh ? "美国配合" : "US"}</option>
                {usRoster.map((r) => <option key={r.username} value={r.username}>{T(r.name)}</option>)}
              </select>
              <button onClick={reassign} data-testid={`concept-reassign-apply-${b.leadId}`} className="text-[11px] text-white bg-slate-700 rounded px-2 py-1">{zh ? "应用" : "Apply"}</button>
            </div>
          )}

          {b.reviewStatus === "rejected" && b.reviewNote && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-[11px] text-red-700" data-testid={`concept-review-note-${b.leadId}`}>
              {zh ? "设计经理退回：" : "Rejected: "}{b.reviewNote}{b.reviewedBy ? ` · ${b.reviewedBy}` : ""}
            </div>
          )}

          {canManage && (b.finalFiles || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100" data-testid={`concept-review-${b.leadId}`}>
              <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" />{zh ? "方案审核（非强制，仅提醒）：" : "Review:"}</span>
              <button onClick={() => review("approved")} data-testid={`concept-review-approve-${b.leadId}`} className="text-[11px] text-white bg-emerald-600 hover:bg-emerald-700 rounded px-2.5 py-1 inline-flex items-center gap-1"><Check className="h-3 w-3" />{zh ? "通过" : "Approve"}</button>
              <button onClick={() => review("rejected")} data-testid={`concept-review-reject-${b.leadId}`} className="text-[11px] text-red-600 border border-red-300 hover:bg-red-50 rounded px-2.5 py-1 inline-flex items-center gap-1"><Undo2 className="h-3 w-3" />{zh ? "退回" : "Reject"}</button>
            </div>
          )}

          <div className="pt-1 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{zh ? "设计 / 部门讨论" : "Discussion"}</p>
            <BriefDiscussion api={api} leadId={b.leadId} zh={zh} />
          </div>
        </div>
      )}
      {qrOpen && b.shareToken && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setQrOpen(false)} data-testid={`concept-brief-qr-modal-${b.leadId}`}>
          <div className="bg-white rounded-xl p-5 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQrOpen(false)} className="ml-auto block text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            <h3 className="text-base font-bold text-slate-800 mb-1">{zh ? "客户方案网页" : "Client concept page"}</h3>
            <p className="text-[11px] text-slate-400 mb-3">{b.clientName} · {b.address}</p>
            <div className="flex justify-center mb-3 bg-white p-2 rounded-lg border border-slate-100">
              <QRCodeCanvas value={`${window.location.origin}/concept/${b.shareToken}`} size={160} data-testid={`concept-brief-qr-${b.leadId}`} />
            </div>
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5">
              <input readOnly value={`${window.location.origin}/concept/${b.shareToken}`} className="flex-1 text-[11px] text-slate-600 bg-transparent outline-none" />
              <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/concept/${b.shareToken}`); toast.success(zh ? "已复制" : "Copied"); }} className="text-slate-500 hover:text-violet-600 text-[11px]">{zh ? "复制" : "Copy"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DesignConceptBriefs({ api, zh }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/design/concept-briefs").then(({ data }) => setD(data)).catch(() => setD({ briefs: [], canManage: false, canSubmit: false, roster: [] })); }, [api]);
  useEffect(() => { load(); }, [load]);
  if (!d || (d.briefs || []).length === 0) {
    if (!d) return null;
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="concept-briefs-section">
        <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5 mb-1"><Inbox className="h-4 w-4" />{zh ? "来自销售的概念方案需求" : "Concept design requests"}</p>
        <p className="text-[12px] text-slate-300 py-3 text-center" data-testid="concept-briefs-empty">{zh ? "暂无概念方案设计需求" : "None"}</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-emerald-200 p-4 mb-4" data-testid="concept-briefs-section">
      <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5 mb-2"><Inbox className="h-4 w-4" />{zh ? "来自销售的概念方案需求" : "Concept design requests"} <span className="text-slate-400 text-xs">({d.briefs.length})</span></p>
      <div className="grid md:grid-cols-2 gap-2">
        {d.briefs.map((b) => <BriefCard key={b.leadId} api={api} zh={zh} b={b} canSubmit={d.canSubmit} canManage={d.canManage} roster={d.roster} onChange={load} />)}
      </div>
    </div>
  );
}
